import {faChevronLeft} from "@fortawesome/pro-solid-svg-icons/faChevronLeft";
import {faChevronRight} from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {wrap} from "@popmotion/popcorn";
import {AnimatePresence, motion} from "framer-motion";
import React, {forwardRef, memo, useEffect, useRef, useState} from "react";

type CarouselType = {
    items: any;
    title?: string;
    style?: any;
    ref?: any;
    isOpen: boolean;
}

const swipeConfidenceThreshold = 10000;
const SwipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
};

const AnimationVariants = {
    enter: (direction: number) => {
        return {
            x: direction > 0 ? 200 : -200,
            opacity: 0
        };
    },
    center: {
        zIndex: 1,
        x: 0,
        opacity: 1
    },
    exit: (direction: number) => {
        return {
            zIndex: 0,
            x: direction < 0 ? 200 : -200,
            opacity: 0
        };
    }
};

const Carousel = memo(forwardRef((props: CarouselType, ref: React.ForwardedRef<HTMLDivElement>) =>  {
    const [[page, direction], setPage] = useState([0, 0]);

    const contentIndex = wrap(0, props.items.length, page);

    const canvasWrapperRef = useRef<HTMLDivElement>(null);
    const canvasInnerWrapperRef = useRef<HTMLDivElement>(null);

    const Paginate = (newDirection: number) => {
        setPage([page + newDirection, newDirection]);
    };

    const JumpTo = (index: number) => {
        setPage([index, page > index ? 0 : 1]);
    }

    const UpdateContainerHeight = () => {
        if(canvasWrapperRef.current && canvasInnerWrapperRef.current && canvasInnerWrapperRef.current.querySelector('div')) {
            canvasWrapperRef.current.style.height = canvasInnerWrapperRef.current.querySelector('div')?.getBoundingClientRect().height + 'px'
        }
    }

    useEffect(() => {
        UpdateContainerHeight();
    }, [props, canvasWrapperRef.current, canvasInnerWrapperRef.current, page, direction]);

    return (
        <div className={`carousel-wrapper ${props.isOpen ? '' : 'hide'}`} style={props.style} ref={ref}>
            {props.title && (
                <div className={'carousel-title'}>
                    <div>{props.title}</div>

                    <CarouselNavigation
                        items={props.items}
                        index={contentIndex}
                        navigationCallback={(val: number) => JumpTo(val)}
                        paginateCallback={(val: number) => Paginate(val)}
                    />

                </div>
            )}

            <div className="carousel" ref={canvasWrapperRef}>
                <AnimatePresence initial={false} custom={direction}>
                    <motion.div
                        key={`${props.title}${page}`}
                        ref={canvasInnerWrapperRef}
                        custom={direction}
                        variants={AnimationVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                            x: { type: "spring", stiffness: 300, damping: 30 },
                            opacity: { duration: 0.1 }
                        }}
                        drag="x"
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={1}
                        onDragEnd={(e, { offset, velocity }) => {
                            const swipe = SwipePower(offset.x, velocity.x);

                            if (swipe < -swipeConfidenceThreshold) {
                                Paginate(1);
                            } else if (swipe > swipeConfidenceThreshold) {
                                Paginate(-1);
                            }
                        }}
                    >
                        {props.items[contentIndex]}
                    </motion.div>
                </AnimatePresence>

{/*                <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="round-button stick-left"
                    onClick={() => paginate(-1)}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                </motion.div>

                <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="round-button stick-right"
                    onClick={() => paginate(1)}>
                    <FontAwesomeIcon icon={faArrowRight} />
                </motion.div>*/}

                {!props.title && (
                    <CarouselNavigation
                        items={props.items}
                        index={contentIndex}
                        navigationCallback={(val: number) => JumpTo(val)}
                        paginateCallback={(val: number) => Paginate(val)}
                    />
                )}
            </div>

        </div>
    )
}));

type CarouselNavigationType = {
    items: any;
    index: number;
    paginateCallback: (e: number) => void;
    navigationCallback: (e: number) => void;
    dotNavigation?: boolean;
}

const CarouselNavigation = (props: CarouselNavigationType) => {
    if(props.dotNavigation) return <CarouselDotNavigation {...props} />

    return (
        <div className={`carousel-navigation-container`}>
            <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className={"carousel-navigation-entry"}
                onClick={() => props.paginateCallback(-1)}
            >
                <FontAwesomeIcon icon={faChevronLeft} />
            </motion.div>
            <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className={"carousel-navigation-entry"}
                onClick={() => props.paginateCallback(1)}
            >
                <FontAwesomeIcon icon={faChevronRight} />
            </motion.div>
        </div>
    )
}

const CarouselDotNavigation = (props: CarouselNavigationType) => {
    return (
        <div className={`carousel-navigation-container`}>
            {props.items.map((item: any, key: number) =>
                <motion.div
                    key={key}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className={props.index === key ? "carousel-navigation-entry active" : "carousel-navigation-entry"}
                    style={{ pointerEvents: props.index === key ? 'none' : 'all' }}
                    onClick={() => props.navigationCallback(key)}>
                </motion.div>
            )}
        </div>
    )
}


export default Carousel;
